import React from 'react';
import { CheckCircle, Zap, FileText, CreditCard, Phone, ExternalLink, Shield, FileCheck } from 'lucide-react';

/**
 * TrustSection - Secțiuni de credibilitate pentru SEO și conversii
 *
 * Include:
 * 1. Document oficial ANCPI (fundal albastru)
 * 2. Ce documente primești? (fundal alb)
 * 3. Ce informații ai nevoie? (fundal gri)
 * 4. De ce să ne alegi? (fundal albastru închis)
 */
const TrustSection = () => {
  return (
    <>
      {/* SECȚIUNEA 1: Document oficial ANCPI */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Document oficial emis de ANCPI
          </h2>

          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Extrasul de Carte Funciară livrat prin intermediul serviciului nostru este emis oficial
            de către ANCPI (Agenția Națională de Cadastru și Publicitate Imobiliară) și poate fi
            acceptat de toate instituțiile statului, bănci, notari și instanțe judecătorești.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 max-w-xl mx-auto">
            <h3 className="text-xl font-semibold mb-3">Verifică autenticitatea documentului</h3>
            <p className="text-blue-100 text-sm mb-4">
              Toate extrasele de Carte Funciară și planurile cadastrale emise prin platforma noastră
              pot fi verificate pe site-ul oficial ANCPI folosind codul de bare de pe prima pagină a documentului.
            </p>

            <a
              href="https://www.ancpi.ro/verificare/dc_index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              VERIFICĂ PE ANCPI.RO
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2: Ce documente primești? */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-12">
            Ce documente vei primi?
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Stânga - Text */}
            <div>
              <p className="text-gray-600 mb-6">
                După finalizarea comenzii, vei primi pe email următoarele documente:
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Extras de Carte Funciară / Plan Cadastral
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Document oficial emis de ANCPI cu semnătură electronică. Autenticitatea poate fi
                      verificată pe site-ul oficial ANCPI folosind codul de bare de pe prima pagină.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Factură fiscală
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Factura include costul serviciului și taxa ANCPI. Pentru persoane juridice,
                      selectează opțiunea "Factură pe firmă" și completează CUI-ul.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Dreapta - Imagine specimen (aceeași ca în wizard) */}
            <div className="relative flex items-center justify-center">
              <img
                src="/carte-funciara-2-specimen.webp"
                alt="Specimen Extras Carte Funciară ANCPI"
                className="w-full max-w-xs sm:max-w-sm rounded-lg shadow-xl border border-gray-100"
                width="1653"
                height="1936"
              />
              {/* Watermark SPECIMEN peste imagine */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-red-600 font-bold text-2xl sm:text-3xl tracking-widest transform -rotate-45 drop-shadow-lg opacity-90">
                  SPECIMEN
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3: Ce informații ai nevoie? */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-12">
            Ce informații ai nevoie pentru a comanda?
          </h2>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <p className="text-gray-600 mb-6">
              Pentru a obține un extras de carte funciară ai nevoie de:
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Număr cadastral (obligatoriu)</h3>
                  <p className="text-gray-600 text-sm">
                    Numărul cadastral este un cod unic atribuit fiecărui imobil. Îl găsești în contractul
                    de vânzare-cumpărare, în actele de proprietate sau pe factura de utilități.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Județul și localitatea</h3>
                  <p className="text-gray-600 text-sm">
                    Selectezi din listă județul și unitatea administrativ-teritorială (UAT) unde se află imobilul.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                <strong>Nu știi numărul cadastral?</strong> Contactează-ne și te ajutăm să îl identifici.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4: De ce să ne alegi? */}
      <section className="bg-gradient-to-r from-gray-800 to-gray-900 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-12">
            De ce să folosești serviciul nostru?
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Rapid */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Rapid</h3>
              <p className="text-gray-300 text-sm">
                Extras livrat pe email în doar 5 minute
              </p>
            </div>

            {/* Oficial */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Oficial</h3>
              <p className="text-gray-300 text-sm">
                Document emis direct de ANCPI, valabil juridic
              </p>
            </div>

            {/* Sigur */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Sigur</h3>
              <p className="text-gray-300 text-sm">
                Plată securizată prin Stripe, date protejate
              </p>
            </div>

            {/* Suport */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Suport</h3>
              <p className="text-gray-300 text-sm">
                Asistență telefonică și email Luni-Vineri, 09:00-17:00
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5: Ce este un extras de carte funciară? */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8">
            Ce reprezintă un Extras de Carte Funciară?
          </h2>

          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            Extrasul de Carte Funciară este un document oficial care conține informații complete despre un imobil:
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-700"><strong>Proprietarii</strong> actuali și istoricul proprietății</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-700"><strong>Sarcinile</strong> (ipoteci, garanții, drepturi de trecere)</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-700"><strong>Suprafața</strong> terenului și a construcțiilor</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-700"><strong>Numărul cadastral</strong> și numărul de carte funciară</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 sm:col-span-2">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-700"><strong>Adresa</strong> completă a imobilului</span>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <p className="text-emerald-800">
              <strong>Documentul este necesar pentru:</strong> tranzacții imobiliare, credite ipotecare,
              procese juridice, verificarea situației juridice a unui imobil.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default TrustSection;
